// Collapsible Financial Snapshot for a single class.
//
// Renders the four canonical financial numbers (Gross, Tithe, Payroll, Net),
// break-even, per-additional-student profit, capacity projection, and — when
// the class has an interest list — a future-session projection.
//
// Every number comes from `computeClassFinancials`. Nothing here is editable.

import { useState } from "react";
import { ChevronDown, DollarSign } from "lucide-react";
import {
  computeClassFinancials,
  healthLabel,
  money,
  moneySigned,
  type ClassFinancialInput,
  type FinancialSnapshot,
} from "@/lib/classFinancials";

interface Props {
  input: ClassFinancialInput;
  /** Collapsed by default — matches the Operations Board default. */
  defaultOpen?: boolean;
  /** Compact = tighter padding for use inside a small card. */
  compact?: boolean;
  /** Small italic note shown under the panel (e.g. "Reporting since April 9, 2026."). */
  reportingNote?: string;
}

export default function FinancialSnapshotPanel({
  input,
  defaultOpen = false,
  compact = false,
  reportingNote,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const snap = computeClassFinancials(input);

  return (
    <div
      className={`rounded-md border border-border bg-card/40 ${compact ? "text-xs" : "text-sm"}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 ${
          compact ? "px-2.5 py-1.5" : "px-3 py-2"
        } text-left`}
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <DollarSign className="h-3.5 w-3.5 text-emerald-700" />
          Financial Snapshot
          {snap.applicable && (
            <span className="text-muted-foreground font-normal">
              · Net {money(snap.net)}
            </span>
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className={compact ? "px-2.5 pb-2.5 pt-0" : "px-3 pb-3 pt-0"}>
          {snap.applicable ? (
            <SnapshotBody snap={snap} />
          ) : (
            <NotApplicable snap={snap} />
          )}
          {reportingNote && snap.applicable && (
            <p className="mt-2 text-[10px] italic text-muted-foreground">
              {reportingNote}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SnapshotBody({ snap }: { snap: FinancialSnapshot }) {
  const health = healthLabel(snap.health);
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-background/70 border border-border/70 divide-y divide-border/60">
        <Row
          label="Gross Revenue"
          value={money(snap.gross)}
          icon="💰"
          valueClass="text-sky-700"
        />
        <Row
          label="Ministry Tithe (10%)"
          value={moneySigned(snap.tithe, "-")}
          icon="🙏"
          valueClass="text-muted-foreground"
        />
        <Row
          label="Instructor Payroll"
          value={moneySigned(snap.instructorPayroll, "-")}
          icon="👩‍🏫"
          valueClass="text-orange-700"
          sub={`${snap.hoursPerSession.toString().replace(/\.0$/, "")}h × ${money(
            snap.instructorPerSession,
          )} × ${snap.totalMeetings} meetings`}
        />
        <Row
          label="Net Profit"
          value={money(snap.net)}
          icon="🌿"
          valueClass="text-emerald-700 font-semibold"
          bold
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MiniStat
          label="Break-even"
          value={`${snap.breakEven} student${snap.breakEven === 1 ? "" : "s"}`}
          icon="📈"
          accent="text-purple-700"
        />
        <MiniStat
          label="Per additional student"
          value={`+${money(snap.profitPerStudent)} profit`}
          icon="💵"
          accent="text-emerald-700"
        />
      </div>

      <div className="flex items-center gap-1.5 text-xs">
        <span>{health.icon}</span>
        <span className="font-medium text-foreground">{health.label}</span>
        <span className="text-muted-foreground">— {health.blurb}</span>
      </div>

      {snap.capacity.capacity != null && (
        <CapacityCard snap={snap} />
      )}

      {snap.futureSession && (
        <FutureSessionCard snap={snap} />
      )}
    </div>
  );
}

function Row({
  label,
  value,
  icon,
  valueClass = "",
  sub,
  bold = false,
}: {
  label: string;
  value: string;
  icon?: string;
  valueClass?: string;
  sub?: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 gap-3">
      <div className="min-w-0">
        <div className={`flex items-center gap-1.5 ${bold ? "font-medium text-foreground" : "text-foreground/90"}`}>
          {icon && <span aria-hidden>{icon}</span>}
          <span className="truncate">{label}</span>
        </div>
        {sub && <div className="text-[11px] text-muted-foreground pl-5">{sub}</div>}
      </div>
      <div className={`tabular-nums ${valueClass}`}>{value}</div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: string;
  icon: string;
  accent: string;
}) {
  return (
    <div className="rounded-md border border-border/70 bg-background/70 px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <span aria-hidden>{icon}</span>
        {label}
      </div>
      <div className={`font-semibold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function CapacityCard({ snap }: { snap: FinancialSnapshot }) {
  const cap = snap.capacity;
  const enrolled = cap.capacity! - (cap.seatsRemaining ?? 0);
  return (
    <div className="rounded-md border border-border/70 bg-background/70 p-3 space-y-1">
      <div className="text-xs font-medium text-foreground">If every seat filled</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">Current enrollment</span>
        <span className="text-right tabular-nums">
          {enrolled} / {cap.capacity}
        </span>
        <span className="text-muted-foreground">Potential revenue</span>
        <span className="text-right tabular-nums text-sky-700">
          {money(cap.potentialGross)}
        </span>
        <span className="text-muted-foreground">Potential net profit</span>
        <span className="text-right tabular-nums text-emerald-700 font-medium">
          {money(cap.potentialNet)}
        </span>
        <span className="text-muted-foreground">Seats remaining</span>
        <span className="text-right tabular-nums">{cap.seatsRemaining}</span>
        <span className="text-muted-foreground">Potential added profit</span>
        <span className="text-right tabular-nums text-emerald-700">
          +{money(cap.potentialAdditional)}
        </span>
      </div>
    </div>
  );
}

function FutureSessionCard({ snap }: { snap: FinancialSnapshot }) {
  const f = snap.futureSession!;
  return (
    <div className="rounded-md border border-purple-200 bg-purple-50/60 p-3 space-y-1">
      <div className="text-xs font-medium text-purple-900">
        Potential future session
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-purple-900/70">Interested families</span>
        <span className="text-right tabular-nums text-purple-900">
          {f.interested} / {f.threshold}
        </span>
        <span className="text-purple-900/70">Projected revenue</span>
        <span className="text-right tabular-nums text-sky-700">
          {money(f.projectedRevenue)}
        </span>
        <span className="text-purple-900/70">Projected profit</span>
        <span className="text-right tabular-nums text-emerald-700 font-medium">
          {money(f.projectedProfit)}
        </span>
      </div>
      <div className="text-[11px] text-purple-900/80 pt-1">{f.unlockMessage}</div>
    </div>
  );
}

function NotApplicable({ snap }: { snap: FinancialSnapshot }) {
  const reason =
    snap.reason === "external"
      ? "Privately managed class — Silver Sage doesn't collect revenue on this one."
      : snap.reason === "no_instructor"
        ? "Assign an instructor and financials will populate automatically."
        : snap.reason === "no_price"
          ? "Set a price and financials will populate automatically."
          : "Add this class's weekday to the Semester Calendar to enable projections.";
  return <div className="text-xs text-muted-foreground py-1">{reason}</div>;
}
